import apiClient, { extractData } from "../api/client";

/**
 * List ZKT biometric devices and last ingest batch.
 * @returns {Promise<{ devices: Array, lastIngest: object|null }>}
 */
export async function getZktDevices() {
  const response = await apiClient.get("/attendance/zkt/devices");
  return extractData(response);
}

/**
 * Daily ZKT attendance for a calendar month (Excel: hours + late per day).
 * @param {number} month
 * @param {number} year
 * @param {{ incompleteOnly?: boolean, beforeToday?: boolean, todayOnly?: boolean, employeeId?: string, branchId?: string, departmentId?: string, date?: string, status?: string, source?: string, deviceId?: string }} [options]
 * @returns {Promise<{ month: number, year: number, count: number, days: Array }>}
 */
export async function getZktPunches(month, year, options = {}) {
  const response = await apiClient.get("/attendance/zkt/punches", {
    params: {
      month,
      year,
      ...(options.incompleteOnly ? { incompleteOnly: true } : {}),
      ...(options.beforeToday ? { beforeToday: true } : {}),
      ...(options.todayOnly ? { todayOnly: true } : {}),
      ...(options.employeeId ? { employeeId: options.employeeId } : {}),
      ...(options.branchId ? { branchId: options.branchId } : {}),
      ...(options.departmentId ? { departmentId: options.departmentId } : {}),
      ...(options.date ? { date: options.date } : {}),
      ...(options.status ? { status: options.status } : {}),
      ...(options.source ? { source: options.source } : {}),
      ...(options.deviceId ? { deviceId: options.deviceId } : {}),
    },
  });
  return extractData(response);
}

/**
 * HR manual check-in / check-out for an incomplete attendance day.
 * @param {string} attendanceId
 * @param {{ checkInTime?: string, checkOutTime?: string, breakOutTime?: string, breakInTime?: string, notes?: string }} payload
 */
export async function updateManualPunches(attendanceId, payload) {
  const response = await apiClient.patch(
    `/attendance/${attendanceId}/manual-punches`,
    payload,
  );
  return extractData(response);
}

/**
 * HR closes a 4-punch day that already has in+out but no break punches.
 * Deducts 1 unpaid rest hour under 9h; extra beyond 8h is overtime.
 * @param {string} attendanceId
 */
export async function completeWithoutBreak(attendanceId) {
  const response = await apiClient.post(
    `/attendance/${attendanceId}/complete-without-break`,
    {},
    { timeout: 60000 },
  );
  return extractData(response);
}

/**
 * HR creates (or upserts) fingerprint punches for employee+date —
 * works even when the employee never punched that day.
 * @param {{ employeeId: string, date: string, checkInTime: string, checkOutTime: string, breakOutTime?: string, breakInTime?: string, notes?: string }} payload
 */
export async function createManualPunches(payload) {
  const response = await apiClient.post("/attendance/manual-punches", payload);
  return extractData(response);
}

/**
 * HR / HR Staff Excel punch-log upload (No. | Date/Time | Location ID).
 * @param {File} file
 */
export async function uploadPunchLogExcel(file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post("/attendance/kmt/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  return extractData(response);
}

/**
 * HR sets day status for flexible-hours (manualAttendanceOnly) employees.
 * @param {{ employeeId: string, date: string, status: string, notes?: string }} payload
 */
export async function upsertManualAttendanceStatus(payload) {
  const response = await apiClient.post("/attendance/manual-status", payload);
  return extractData(response);
}

/**
 * HR permanently deletes fingerprint punches + attendance for a single day.
 * Later device sync will not recreate that day.
 * @param {string} attendanceId
 * @returns {Promise<{ attendanceId: string, employeeId: string, employeeName: string, date: string, deletedPunches: number }>}
 */
export async function deleteAttendanceDayFingerprints(attendanceId) {
  const response = await apiClient.delete(
    `/attendance/${attendanceId}/fingerprints`,
  );
  return extractData(response);
}

/**
 * Monthly KMT attendance summary per employee (populated by ZKT sync).
 * @param {number} month
 * @param {number} year
 * @param {{ branchId?: string, departmentId?: string, employeeId?: string }} [filters]
 */
export async function getMonthlySummary(month, year, filters = {}) {
  const response = await apiClient.get("/attendance/monthly-summary", {
    params: {
      month,
      year,
      ...(filters.branchId ? { branchId: filters.branchId } : {}),
      ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
      ...(filters.employeeId ? { employeeId: filters.employeeId } : {}),
    },
    timeout: 60000,
  });
  return extractData(response);
}
